/**
 * The History page's filter-chip row — README §9's "Six filter pills,
 * selected = --teal-700 fill; channel filters carry their channel icon.",
 * bound to the store's `hFilter` (already scoped as `HistoryFilter` in
 * src/store.ts, the exact same six literals `SearchScope` uses
 * server-side).
 */
import type { HistoryFilter } from '../../store'
import { useStore } from '../../store'
import { HISTORY_FILTERS } from './format'
import { CHANNEL_ICON } from '../rail/format'
import { Icon } from '../ui'
import type { IconName } from '../ui'

/** Which of the six filters is a channel filter, and its icon — README §9. `everything`/`closed`/`live` are not channels, so they carry none. */
const FILTER_ICON: Partial<Record<HistoryFilter, IconName>> = {
  chat: CHANNEL_ICON.chat,
  sms: CHANNEL_ICON.sms,
  calls: CHANNEL_ICON.voice,
}

export function FilterChips() {
  const hFilter = useStore((s) => s.hFilter)
  const set = useStore((s) => s.set)

  return (
    <div role="group" aria-label="Filter" className="flex flex-wrap items-center gap-1.5">
      {HISTORY_FILTERS.map((f) => {
        const selected = hFilter === f.value
        const icon = FILTER_ICON[f.value]
        return (
          <button
            key={f.value}
            type="button"
            aria-pressed={selected}
            data-testid={`history-filter-${f.value}`}
            onClick={() => set({ hFilter: f.value })}
            className={[
              'inline-flex items-center gap-1.5 rounded-[18px] border px-3 py-1 text-[12px] font-medium transition-colors',
              // README §7's own selected treatment applied here too: a solid
              // --brand-primary fill with --text-on-brand, not the pale-tint
              // "border + tinted background" style this used to share with
              // an unselected chip's own border colour.
              selected ? 'border-cc-blue bg-cc-blue text-on-brand' : 'border-subtle text-lo',
            ].join(' ')}
          >
            {icon ? <Icon name={icon} size={12} /> : null}
            {f.label}
          </button>
        )
      })}
    </div>
  )
}
